import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCollection, getSiteLabel, requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const actorLabels = { store: 'Magasin', workshop: 'Atelier', sav: 'Logistique', admin: 'Direction', unknown: 'Application' };
const entityLabels = { commande: 'JOB / suivi atelier', sav: 'Dossier SAV', bon_livraison: 'Bon de livraison' };

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR');
}

function snapshotText(value) {
  if (!value) return '';
  try { return JSON.stringify(value, null, 2); } catch (_error) { return String(value); }
}

export default function ActivityJournalPage() {
  const menuPage = useMenuPage('/journal');
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ search: '', entityType: '', action: '', actorType: '', siteId: '' });
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadJournal = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
      const payload = await requestJson(`/activity?${params.toString()}`);
      setRows(Array.isArray(payload?.data) ? payload.data : []);
      setActions(Array.isArray(payload?.actions) ? payload.actions : []);
      setPagination(payload?.pagination || { page, limit: 50, total: 0, pages: 0 });
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { loadJournal(); }, [loadJournal]);
  useEffect(() => {
    requestJson('/sites?limit=500')
      .then((payload) => setSites(getCollection(payload)))
      .catch(() => setSites([]));
  }, []);
  const updateFilter = (key, value) => { setPage(1); setFilters((current) => ({ ...current, [key]: value })); };
  const entityOptions = useMemo(() => Object.entries(entityLabels), []);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1700, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary.main" sx={{ fontWeight: 900 }}>ADMINISTRATION</Typography>
          <Typography variant="h4" sx={{ fontWeight: 950 }}>{menuPage.pageTitle || 'Journal'}</Typography>
          <Typography color="text.secondary">Toutes les actions effectuées sur les JOB, dossiers SAV et bons de livraison.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadJournal}>Rafraîchir</Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField size="small" label="Recherche" placeholder="JOB, SAV, BL, action, détail…" value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} sx={{ flex: 2 }} />
            <TextField size="small" select label="Type" value={filters.entityType} onChange={(event) => updateFilter('entityType', event.target.value)} sx={{ minWidth: 190 }}>
              <MenuItem value="">Tous</MenuItem>
              {entityOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Action" value={filters.action} onChange={(event) => updateFilter('action', event.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">Toutes</MenuItem>
              {actions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Auteur" value={filters.actorType} onChange={(event) => updateFilter('actorType', event.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">Tous</MenuItem>
              {Object.entries(actorLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Site" value={filters.siteId} onChange={(event) => updateFilter('siteId', event.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">Tous les sites</MenuItem>
              {sites.map((site) => <MenuItem key={site._id} value={site._id}>{getSiteLabel(site) || site.code || site.name}</MenuItem>)}
            </TextField>
            <Button variant="text" onClick={() => { setPage(1); setFilters({ search: '', entityType: '', action: '', actorType: '', siteId: '' }); }}>Réinitialiser</Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
            <HistoryRoundedIcon color="primary" />
            <Typography sx={{ fontWeight: 900 }}>{pagination.total || 0} action(s)</Typography>
          </Stack>
          {loading ? <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Élément</TableCell><TableCell>Action</TableCell><TableCell>Auteur</TableCell><TableCell>Détail / valeurs</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.occurredAt)}</TableCell>
                      <TableCell><Typography sx={{ fontWeight: 850 }}>{row.reference || '—'}</Typography><Typography variant="caption" color="text.secondary">{entityLabels[row.entityType] || row.entityType}</Typography></TableCell>
                      <TableCell><Chip size="small" color={row.action.includes('correct') || row.action.includes('deleted') ? 'warning' : 'primary'} label={row.label || row.action} /></TableCell>
                      <TableCell><Typography variant="body2">{row.actorName || 'Application'}</Typography><Typography variant="caption" color="text.secondary">{actorLabels[row.actorType] || row.actorType}{row.actorSiteLabel ? ` · ${row.actorSiteLabel}` : ''}</Typography></TableCell>
                      <TableCell sx={{ minWidth: 300 }}><Typography variant="body2">{row.detail || '—'}</Typography>{(row.before || row.after) && <Box component="details" sx={{ mt: 0.5 }}><Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main', fontSize: 12 }}>Voir avant / après</Box><Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto' }}>{snapshotText({ avant: row.before, après: row.after })}</Typography></Box>}</TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}><Typography color="text.secondary">Aucune action trouvée.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pagination.pages > 1 && <Stack alignItems="center" sx={{ p: 2 }}><Pagination page={page} count={pagination.pages} onChange={(_event, value) => setPage(value)} color="primary" /></Stack>}
        </CardContent>
      </Card>
    </Box>
  );
}
